import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, AlertCircle, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { masterSettingsAPI } from '@/api/api';

const FineSetting = () => {
  const [fines, setFines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    fine_type: 'Late Fee',
    description: '',
    amount: '',
    calculation_method: 'Fixed',
    percentage: '',
    grace_period_days: '',
    is_active: true
  });
  const [editingId, setEditingId] = useState(null);

  const fineTypes = [
    'Late Fee',
    'Library Fine',
    'Library Damage Fine',
    'Hostel Damage Fine',
    'Exam Late Fee',
    'Late Admission Fee',
    'Document Reissue Fine',
    'ID Card Reissue Fine',
    'Other'
  ];

  useEffect(() => {
    fetchFines();
  }, []);

  const fetchFines = async () => {
    try {
      const response = await masterSettingsAPI.getFineSettings();
      setFines(response.data);
    } catch (error) {
      console.error('Error fetching fines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        percentage: formData.percentage ? parseFloat(formData.percentage) : null,
        grace_period_days: formData.grace_period_days ? parseInt(formData.grace_period_days) : 0
      };

      if (editingId) {
        await masterSettingsAPI.updateFineSetting(editingId, data);
      } else {
        await masterSettingsAPI.createFineSetting(data);
      }
      fetchFines();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving fine:', error);
      alert(error.response?.data?.detail || 'Failed to save fine setting');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this fine setting?')) {
      try {
        await masterSettingsAPI.deleteFineSetting(id);
        fetchFines();
      } catch (error) {
        console.error('Error deleting fine:', error);
        alert('Failed to delete fine setting');
      }
    }
  };

  const handleEdit = (fine) => {
    setFormData({
      fine_type: fine.fine_type,
      description: fine.description || '',
      amount: fine.amount,
      calculation_method: fine.calculation_method,
      percentage: fine.percentage || '',
      grace_period_days: fine.grace_period_days || '',
      is_active: fine.is_active
    });
    setEditingId(fine.id);
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      fine_type: 'Late Fee',
      description: '',
      amount: '',
      calculation_method: 'Fixed',
      percentage: '',
      grace_period_days: '',
      is_active: true
    });
    setEditingId(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <AlertCircle className="text-blue-600" size={32} />
            General Fines
          </h1>
          <p className="text-gray-600 mt-1">Configure fines for Library, Hostel, Late Fee, etc.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus size={20} className="mr-2" />
              Add Fine Setting
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Fine Setting' : 'Add New Fine Setting'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fine Type *
                  </label>
                  <select
                    value={formData.fine_type}
                    onChange={(e) => setFormData({ ...formData, fine_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    {fineTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Calculation Method *
                  </label>
                  <select
                    value={formData.calculation_method}
                    onChange={(e) => setFormData({ ...formData, calculation_method: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="Fixed">Fixed Amount</option>
                    <option value="Percentage">Percentage Based</option>
                    <option value="Per Day">Per Day</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="2"
                  placeholder="Brief description of the fine"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount (₹) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.calculation_method === 'Per Day' ? 'Amount charged per day' : 
                     formData.calculation_method === 'Fixed' ? 'Fixed fine amount' : 
                     'Base amount for percentage calculation'}
                  </p>
                </div>
                {formData.calculation_method === 'Percentage' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.percentage}
                      onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., 5"
                    />
                    <p className="text-xs text-gray-500 mt-1">Percentage of base amount</p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grace Period (Days)
                  </label>
                  <input
                    type="number"
                    value={formData.grace_period_days}
                    onChange={(e) => setFormData({ ...formData, grace_period_days: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Days before fine applies</p>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                  Active
                </label>
              </div>

              {/* Example Calculation */}
              {formData.amount && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-blue-900 mb-2">Example Calculation</h4>
                  {formData.calculation_method === 'Fixed' && (
                    <p className="text-sm text-blue-800">
                      Fine Amount: {formatCurrency(formData.amount)}
                    </p>
                  )}
                  {formData.calculation_method === 'Per Day' && (
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>Per Day: {formatCurrency(formData.amount)}</p>
                      <p>5 Days: {formatCurrency(formData.amount * 5)}</p>
                      <p>10 Days: {formatCurrency(formData.amount * 10)}</p>
                    </div>
                  )}
                  {formData.calculation_method === 'Percentage' && formData.percentage && (
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>On ₹1,000: {formatCurrency(1000 * (formData.percentage / 100))}</p>
                      <p>On ₹5,000: {formatCurrency(5000 * (formData.percentage / 100))}</p>
                      <p>On ₹10,000: {formatCurrency(10000 * (formData.percentage / 100))}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-3 justify-end pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingId ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <AlertCircle className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Fine Types</p>
              <p className="text-2xl font-bold text-gray-900">{fines.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Settings</p>
              <p className="text-2xl font-bold text-gray-900">
                {fines.filter(f => f.is_active).length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <DollarSign className="text-orange-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Inactive Settings</p>
              <p className="text-2xl font-bold text-gray-900">
                {fines.filter(f => !f.is_active).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Fine Settings List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fine Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Calculation Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Grace Period
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fines.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    No fine settings found. Add your first fine configuration.
                  </td>
                </tr>
              ) : (
                fines.map((fine) => (
                  <tr key={fine.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="text-orange-500" size={18} />
                        <span className="text-sm font-medium text-gray-900">{fine.fine_type}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {fine.description || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {fine.calculation_method}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-semibold text-gray-900">
                        {formatCurrency(fine.amount)}
                        {fine.calculation_method === 'Per Day' && <span className="text-xs text-gray-500"> /day</span>}
                        {fine.calculation_method === 'Percentage' && fine.percentage && (
                          <span className="text-xs text-gray-500"> ({fine.percentage}%)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {fine.grace_period_days || 0} days
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        fine.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {fine.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(fine)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(fine.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FineSetting;
